import saveArticleMobileMock from "@/public/assets/img/save-article-mobile-mock.png"
import mobileDownloadQrCode from "@/public/landing-page/qr-code_v3.png"
import Link from "@/src/common/components/Link"
import Modal from "@/src/common/components/Modal"
import { SocialShareCard } from "@/src/common/components/SocialShareCard"
import useElementObserver from "@/src/common/hooks/useElementObserver"
import useVisibilityChange from "@/src/common/hooks/useVisibilityChange"
import { normalizeSlug } from "@/src/common/utils"
import useOutsideAlerters from "@/src/elections/business-layer/hooks/useOutsideAlerters"
import { SocialShareEventProps } from "@/src/elections/business-layer/types"
import { nextAuthOption } from "@/src/pages/api/auth/[...nextauth]"
import { READING_TIME_BENCHMARK_SEC } from "@/src/sb/business-layer/config"
import { URLFactory } from "@/src/sb/business-layer/factories/URLFactory"
import { ArticlePageData, Report } from "@/src/sb/business-layer/types"
import { GetArticlePropsUsecase } from "@/src/sb/business-layer/usecases/GetArticlePropsUsecase"
import { AccessDeniedContent } from "@/src/sb/components/AccessDenied"
import { useAnalytics } from "@/src/sb/components/Analytics"
import RelatedIntelligence from "@/src/sb/components/ArticleFeed/RelatedIntelligence"
import Button from "@/src/sb/components/Button"
import B2BPaywallV3 from "@/src/sb/components/Paywall/B2BPaywallV3"
import Ribbon from "@/src/sb/components/icon/Ribbon"
import Share from "@/src/sb/components/icon/Share"
import X from "@/src/sb/components/icon/X"
import DefaultLayoutV3 from "@/src/sb/components/layouts/DefaultLayoutV3"
import { MOBILE_APP_DOWNLOAD_LINK } from "@/src/sb/constants/configs"
import { pages } from "@/src/sb/constants/copy"
import { ENABLE_ACCESS_DENIED_PAGE_V2 } from "@/src/sb/constants/featureFlags"
import { isReportFree } from "@/src/sb/core/utils/helpers"
import useFlourishScript from "@/src/sb/hooks/useFlourishScript"
import { useUser } from "@/src/sb/hooks/useUser"
import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import * as Sentry from "@sentry/nextjs"
import classNames from "classnames"
import { format } from "date-fns"
import fs from "fs"
import { GetServerSidePropsContext, GetServerSidePropsResult } from "next"
import { getServerSession } from "next-auth"
import { JWT } from "next-auth/jwt"
import Image from "next/image"
import Papa from "papaparse"
import path from "path"
import {
  MouseEventHandler,
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState
} from "react"

const BASE_URL = "https://www.stears.co"

const ARTICLE_ACTIONS_POPUP_OPTIONS: {
  topFullRight: string
  bottomFullRight: string
  bottomRight: string
} = {
  topFullRight: "top-full right-0",
  bottomFullRight: "bottom-full right-0",
  bottomRight: "bottom-0 right-0"
}

const ArticleActions = ({
  pageTitle,
  pageURL,
  onClickShare,
  socialIconSize,
  extraClassName,
  popupPosition = "topFullRight"
}: {
  article: Report
  pageTitle: string
  pageURL: string
  onClickShare?: (media: SocialShareEventProps["type"]) => void
  onClickSave?: () => void
  hideTopicButton?: boolean
  buttonsSize?: "default" | "sm"
  socialIconSize?: number
  extraClassName?: string
  popupPosition?: keyof typeof ARTICLE_ACTIONS_POPUP_OPTIONS
}) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [showSharePopup, setShowSharePopup] = useState(false)
  const [isMobile] = useWindowSize()
  const shareCard = useRef<Array<{ el: HTMLElement | null } | null>>([])

  useOutsideAlerters(shareCard, () => {
    setShowSharePopup(false)
  })

  return (
    <section
      className={classNames(
        "flex flex-wrap items-center gap-4",
        extraClassName
      )}
    >
      <div className="relative" ref={(el) => (shareCard.current[0] = { el })}>
        <button
          onClick={() => setShowSharePopup(!showSharePopup)}
          className={"dark:text-gray-400"}
        >
          <Share size={20} />
        </button>

        <section
          className={classNames(
            "absolute dark:text-gray-400 bg-stears_white dark:bg-[#1A1D21] rounded dark:rounded-none shadow-lg my-2 font-jakarta z-10",
            showSharePopup ? "block" : "hidden",
            ARTICLE_ACTIONS_POPUP_OPTIONS[popupPosition]
          )}
        >
          <SocialShareCard
            title={pageTitle}
            url={pageURL}
            expanded={true}
            socialIconSize={socialIconSize}
            buttons={{
              whatsapp: false,
              telegram: false,
              facebook: false,
              "web-share": false
            }}
            onClickShare={onClickShare}
          />
        </section>
      </div>

      <div className="relative hidden">
        <button
          onClick={() => setShowTooltip(true)}
          className={"text-gray-400"}
        >
          <Ribbon size={20} />
        </button>

        {showTooltip ? (
          isMobile ? (
            <Modal
              onClose={() => setShowTooltip(false)}
              className="md:max-w-[400px] dark:bg-[#1A1D21]"
            >
              <section className="font-jakarta">
                <header className="space-y-4 md:hidden">
                  <h3 className="text-xl font-bold leading-8">
                    Available soon on web! Use the app for now.
                  </h3>
                  <p className="font-jakarta">
                    To start enjoying our intuitive mobile app download via the
                    link below
                  </p>
                </header>
                <main className="mt-10 space-y-7 relative md:hidden">
                  <Button
                    type="cta"
                    roundness="edge"
                    stretch
                    href={MOBILE_APP_DOWNLOAD_LINK}
                  >
                    <span className="text-lg font-medium">Download here</span>
                  </Button>

                  <section
                    style={{ height: saveArticleMobileMock.height / 2 - 32 }}
                  >
                    {/* This acts like a placeholder for the image below */}
                  </section>

                  <section className="flex justify-center absolute w-full -bottom-8">
                    <Image
                      src={saveArticleMobileMock.src}
                      width={saveArticleMobileMock.width / 2}
                      height={saveArticleMobileMock.height / 2}
                      alt=""
                    ></Image>
                  </section>
                </main>
              </section>
            </Modal>
          ) : (
            <div className="absolute top-full right-0 w-[250px] bg-stears_white dark:bg-[#1A1D21] p-5 rounded dark:rounded-none shadow-lg space-y-5 mt-2 font-jakarta z-10">
              <section className="space-y-[10px]">
                <header className="flex">
                  <h5 className="font-bold">
                    Available soon on web! Use the app for now.
                  </h5>

                  <button
                    className="shrink-0 flex justify-center items-center dark:bg-gray-600 w-6 h-6 mt-1"
                    onClick={() => setShowTooltip(false)}
                  >
                    <X size={20} />
                  </button>
                </header>

                <p className="text-xs font-light">
                  Download the mobile app by scanning the QR code below.
                </p>
              </section>

              <div>
                <Image
                  src={mobileDownloadQrCode.src}
                  alt="QR Code"
                  width={213}
                  height={213}
                  className="mx-auto"
                />
              </div>
            </div>
          )
        ) : null}
      </div>
    </section>
  )
}

const ViewArticle = ({
  layoutProps = {},
  article,
  recommendedReports,
  articleIsTruncated,
  isAnonymous
}: ArticlePageData) => {
  const analytics = useAnalytics()
  const user = useUser()

  const articleBodyNode = useRef<HTMLDivElement | null>(null)
  const paywallNode = useRef<HTMLDivElement | null>(null)
  const articleBodyWrapperNode = useRef<HTMLDivElement | null>(null)
  const mainPageNode = useRef<HTMLDivElement | null>(null)
  const [articleReadProgress, setArticleReadProgress] = useState(0)
  const articleBodyStartNode = useRef<HTMLDivElement | null>(null)
  const articleFinishNode = useRef<HTMLDivElement | null>(null)
  const [pageTitle, setPageTitle] = useState<string>("")
  const timeUserStartsReadingUnix = useRef(0)
  const timeArticleLostFocusUnix = useRef(0)
  const accumulatedFocusLostTimeSecs = useRef(0)
  const { tabIsVisible } = useVisibilityChange()
  useFlourishScript(true, [])

  const isAccessDeniedPageV2Enabled =
    layoutProps.features?.[ENABLE_ACCESS_DENIED_PAGE_V2]

  const pageURL = URLFactory.absolute(
    "report",
    article.slug,
    isReportFree(article)
  )

  const defaultEventProperties = {
    article_title: article.title,
    article_slug: article.slug,
    article_topic_slug: article.mainTopic?.slug ?? "",
    article_type: article.type
  }

  useElementObserver(
    paywallNode,
    () => {
      analytics?.seePaywall(defaultEventProperties)

      return true
    },
    { options: { threshold: 1 } }
  )

  useElementObserver(articleBodyStartNode, () => {
    // This is the total time in secs the user had the article opened in
    // their browser.
    // Not really relevant information
    const totalTimeSpentOnArticleSec = timeUserStartsReadingUnix.current
      ? Math.round(new Date().getTime() / 1000) -
        timeUserStartsReadingUnix.current
      : 0

    // This is the total time in secs the user actually focuses on the opened
    // article tab in their browser.
    const timeSpentReadingSec =
      totalTimeSpentOnArticleSec - accumulatedFocusLostTimeSecs.current
    analytics?.readArticle({
      ...defaultEventProperties,
      time_spent_reading_sec: timeSpentReadingSec
    })

    return true
  })

  useElementObserver(
    articleFinishNode,
    () => {
      const timeSpentReadingSec =
        Math.round(new Date().getTime() / 1000) -
        timeUserStartsReadingUnix.current
      if (
        timeSpentReadingSec > READING_TIME_BENCHMARK_SEC &&
        !articleIsTruncated
      ) {
        // user has spent more than READING_TIME_BENCHMARK_SEC minute reading on an un-truncated article
        analytics?.finishArticle({
          ...defaultEventProperties,
          time_spent_reading_sec: timeSpentReadingSec
        })

        return true
      }
    },
    {
      options: { threshold: 1 }
    }
  )

  useEffect(() => {
    if (user.loading) return

    if (!(articleIsTruncated && tabIsVisible && user.userType === "subscriber"))
      return

    location.reload()
  }, [tabIsVisible, user.userType])

  useEffect(() => setPageTitle(document.title), [])
  useEffect(() => {
    analytics?.viewArticle(defaultEventProperties)
    timeUserStartsReadingUnix.current = Math.round(new Date().getTime() / 1000)
  }, [])
  useEffect(() => {
    if (tabIsVisible) {
      accumulatedFocusLostTimeSecs.current += timeArticleLostFocusUnix.current
        ? Math.round(new Date().getTime() / 1000) -
          timeArticleLostFocusUnix.current
        : 0
    } else {
      timeArticleLostFocusUnix.current = Math.round(new Date().getTime() / 1000)
    }
  }, [tabIsVisible])

  const handleArticleContentClicked = (
    e: ReactMouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    if (!(e.target instanceof HTMLAnchorElement)) return
    const anchor = e.target
    analytics?.articleContentClicked({
      ...defaultEventProperties,
      label: anchor.innerText,
      url: anchor.href,
      type: "link"
    })
  }

  const handleContentScroll = (e: any) => {
    setArticleReadProgress(
      (e.target?.scrollTop /
        (e.target?.scrollHeight - e.target?.clientHeight)) *
        100
    )
  }

  const handleClickShare = (type: SocialShareEventProps["type"]) => {
    analytics.shareClicked({
      ...defaultEventProperties,
      type
    })
  }

  const disableWriterProfileLink: {
    className: string
    onClick: MouseEventHandler
    "aria-disabled": boolean
  } = {
    className: "cursor-not-allowed",
    onClick: (e) => e.preventDefault(),
    "aria-disabled": true
  }

  if (isAccessDeniedPageV2Enabled && articleIsTruncated) {
    return (
      <DefaultLayoutV3 {...layoutProps} showNavBar={true} setNextQueryParam>
        <AccessDeniedContent showSignInButton={isAnonymous} />
      </DefaultLayoutV3>
    )
  }

  return (
    <>
      <DefaultLayoutV3
        {...layoutProps}
        showNavBar={true}
        setNextQueryParam
        onScroll={handleContentScroll}
        topBarAddon={
          <section className={"h-[1px]"}>
            <section
              className={"h-full bg-gray-600 dark:bg-stears_white"}
              style={{ width: `${articleReadProgress}%` }}
            >
              {/* Article read progress bar */}
            </section>
          </section>
        }
      >
        {article.mainTopic ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: article.mainTopic.name,
                    item: URLFactory.topicPage(article.mainTopic.slug)
                  }
                ]
              })
            }}
          />
        ) : null}

        {article.seo?.jsonSchema ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(article.seo.jsonSchema)
            }}
          ></script>
        ) : null}

        <section
          className="space-y-5 xl:space-y-0 xl:flex justify-center gap-5 p-2.5 md:p-5 mx-auto"
          ref={mainPageNode}
        >
          <section
            className="grow max-w-[740px] mx-auto xl:mx-0 report-platformisation-wrapper"
            ref={articleBodyWrapperNode}
          >
            <header className="article-header">
              <section className="space-y-5">
                <h1 className="font-jakarta font-medium text-xl">
                  {article.title}
                </h1>

                <section className="flex justify-between items-center">
                  <div className="space-x-3 text-gray-600 text-sm dark:text-gray-400">
                    <span className="text-gray-600 dark:text-gray-400 font-light">
                      {format(new Date(article.date), "MMM dd, yyy")}
                    </span>
                    <span>|</span>
                    {article.author.slug ? (
                      <Link
                        {...disableWriterProfileLink}
                        href={URLFactory.writer(article.author.slug)}
                      >
                        {article.author.name}
                      </Link>
                    ) : (
                      <span>{article.author.name}</span>
                    )}
                  </div>

                  <div className="shrink-0">
                    <ArticleActions
                      pageURL={pageURL}
                      pageTitle={pageTitle}
                      article={article}
                      onClickShare={handleClickShare}
                    />
                  </div>
                </section>
              </section>

              <div className="border-b-[0.5px] dark:border-gray-600" />
            </header>

            <main>
              <div
                className="h-[0.5px] bg-transparent border-x-[0.5px] dark:border-gray-600 dark:bg-[#0E0F10]"
                ref={articleBodyStartNode}
              />

              <section className="relative">
                <section
                  className="report-platformisation"
                  ref={articleBodyNode}
                  onClick={handleArticleContentClicked}
                  dangerouslySetInnerHTML={{ __html: article.body ?? "" }}
                />
                {articleIsTruncated ? <div className="paywall-fade" /> : null}
              </section>

              {articleIsTruncated ? (
                <div className="paywall" ref={paywallNode}>
                  <B2BPaywallV3
                    requireSubscription={article.type === "premium"}
                    extraEventProps={defaultEventProperties}
                  />
                </div>
              ) : null}
              <div
                className="h-[1px] bg-transparent border-x-[0.5px] border-t-[0.5px] dark:border-gray-600 dark:bg-[#0E0F10]"
                id={"article-finish-node"}
                ref={articleFinishNode}
              />
              <footer className="article-footer">
                <section className="flex justify-end items-center">
                  <div className="shrink-0">
                    <ArticleActions
                      pageURL={pageURL}
                      pageTitle={pageTitle}
                      article={article}
                      onClickShare={handleClickShare}
                      popupPosition={"bottomFullRight"}
                    />
                  </div>
                </section>
              </footer>
            </main>
          </section>

          <section className="flex flex-col gap-5 w-full max-w-[740px] xl:max-w-[380px] mx-auto xl:mx-0 relative grow">
            <section
              className={
                "sticky top-5 right-0 left-0 md:max-h-[calc(100vh-80px-40px)]"
              }
            >
              <RelatedIntelligence
                heading="Related"
                moreHref={
                  article.mainTopic?.slug
                    ? URLFactory.topicPage(article.mainTopic.slug)
                    : "/home"
                }
                intelligence={recommendedReports}
                onClickAction={({
                  title,
                  slug,
                  type: article_type = "",
                  mainTopic
                }) =>
                  analytics?.articleClicked({
                    article_title: title,
                    article_slug: slug,
                    article_topic_slug: mainTopic?.slug ?? "",
                    article_type,
                    context: "recommended_articles"
                  })
                }
              />
            </section>
          </section>
        </section>
      </DefaultLayoutV3>
    </>
  )
}

export async function getServerSideProps(
  context: GetServerSidePropsContext<{ slug: string }>
): Promise<GetServerSidePropsResult<ArticlePageData>> {
  // Fetch the article as usual
  const token = (
    await getServerSession(
      context.req,
      context.res,
      await nextAuthOption({ res: context.res })
    )
  )?.user as JWT
  const urlSlug = context.params?.slug || ""
  const articleSlug = normalizeSlug(urlSlug)

  const csvPath = path.resolve(
    process.cwd(),
    "public/data/archived-transaction-brief.csv"
  )
  let csvData: any[] = []
  if (fs.existsSync(csvPath)) {
    const csvContent = fs.readFileSync(csvPath, "utf8")
    const parsed = Papa.parse(csvContent, { header: true })
    if (parsed.data && Array.isArray(parsed.data)) {
      csvData = parsed.data
    }
  }

  const getSlugFromUrl = (url: string) => {
    try {
      const pathname = new URL(url, BASE_URL).pathname
      const segments = pathname.split("/").filter(Boolean)
      return normalizeSlug(segments[segments.length - 1] || "")
    } catch {
      const segments = url.split("/").filter(Boolean)
      return normalizeSlug(segments[segments.length - 1] || "")
    }
  }

  const matched = csvData.find(
    (row) => getSlugFromUrl(row.transaction_brief_url) === articleSlug
  )

  if (matched && matched.serial_no) {
    return {
      redirect: {
        destination: `${pages.transactions}${matched.serial_no}`,
        permanent: true
      }
    }
  }

  let articlePageData: ArticlePageData | null = null
  try {
    articlePageData = await new GetArticlePropsUsecase().execute(
      context,
      token ? (token.accessToken as string) : undefined
    )
  } catch (e) {
    Sentry.captureException(e)
  }

  // If no article, return not found
  if (
    !articlePageData?.article ||
    !Object.keys(articlePageData.article).length
  ) {
    return { notFound: true }
  }

  return {
    props: articlePageData
  }
}

export default ViewArticle
